import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getLivePulseSnapshot, getNationRivalry } from "@/lib/social.functions";
import { deriveGlobalWorldStatus, type GlobalWorldStatus } from "@/lib/global/globalMatchEventsEngine";
import { getDangerSnapshot } from "@/lib/dangerPlayMode";
import { setDangerActive } from "@/lib/launchHardening/priorityVisualQueue";

/**
 * Shared (cached by react-query) global world status.
 * Reuses the same query keys as cinematic moments → zero extra fetches.
 */
export function useGlobalWorldStatus(opts?: { matchMinute?: number }): GlobalWorldStatus {
  const pulse = useQuery({
    queryKey: ["social", "pulse_snap"],
    queryFn: () => getLivePulseSnapshot(),
    refetchInterval: 15_000,
  });
  const rivalry = useQuery({
    queryKey: ["social", "nation_rivalry"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 20_000,
  });

  return useMemo(() => {
    let danger = false;
    try { danger = !!getDangerSnapshot()?.active; } catch { danger = false; }
    setDangerActive(danger);
    return deriveGlobalWorldStatus({
      activeFans: pulse.data?.active_fans ?? 0,
      hypeBurst: pulse.data?.hype_burst ?? 0,
      rivalry: (rivalry.data?.rows ?? []).map((r) => ({
        nation_code: r.nation_code ?? "",
        hype_total: r.hype_total,
      })),
      matchMinute: opts?.matchMinute,
      dangerMode: danger,
    });
  }, [pulse.data, rivalry.data, opts?.matchMinute]);
}